import {
    HttpEventType,
    HttpProgressEvent,
} from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    ClientErrorCodes,
    IMLPackage,
    ISignedUrlResponse,
    MLInputType,
    MLLanguage,
    MLProcessor,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import {
    FileUploadService,
    MLPackagesService,
} from '@services';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import UiValidators, {
    UI_DESCRIPTION_MAX_LENGTH,
    UI_MLPACKAGE_NAME_REGEX,
    UI_NAME_MLPACKAGE_MAX_LENGTH,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
    of,
    Subject,
    timer,
} from 'rxjs';
import {
    auditTime,
    catchError,
    delay,
    distinctUntilChanged,
    filter,
    flatMap,
    map,
    pairwise,
    pluck,
    share,
    skip,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

const TRANSITION_SPEED = 250;

@Component({
    templateUrl: './mlpackage-upload.modal.html',
    styleUrls: [
        './mlpackage-upload.modal.scss',
        '../../../../../../styles/modal/file-upload.modal.scss',
    ],
    providers: [
        MLPackagesService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class MLPackageUploadModalComponent
    extends UiModalComponent<any>
    implements OnInit, OnDestroy {

    public files: File[] = [];
    public type!: string;

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);
    public progress$!: Observable<number>;
    public showProgressBar$!: Observable<boolean>;
    public isReadOnly$ = new Subject();
    public resultSize = MAX_NUMBER_OF_RESULTS;

    public processorTypes: ISuggestValue[] = [];
    public inputTypes: ISuggestValue[] = [];
    public languages: ISuggestValue[] = [];

    public isUploadingNewVersion = this.context.data.name != null;

    private readonly _maxModelSizeInByte = 5368709120;  // 5GB

    public get fileNames() {
        return this.files.map(file => file.name).join(', ');
    }

    public get selectedType() {
        return this.form && this.processorTypes.filter(processorType => processorType.id === this.form.get('processor')!.value[0].id);
    }

    public get selectedInputType() {
        return this.inputTypes.filter(inputVal => inputVal.id === this.context.data.inputType);
    }

    public get selectedLanguage() {
        return this.form && this.languages.filter(language => language.id === this.form.get('language')!.value[0].id);
    }

    public get packagingInstructionsUrl() {
        return this._appState.settings.get(Setting.mlPackagingInstructionsUrl);
    }

    private _allowedFileExtensions: string[] = ['.zip'];

    private _mlpackagesService = this._injector.get<MLPackagesService>(MLPackagesService);
    private _fileUploadService = this._injector.get<FileUploadService>(FileUploadService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    private _progressSource = new Subject<number>();
    private _destroyed$ = new Subject();

    ngOnInit() {
        // hide progress for fast uploads
        // and show the progress bar for multiple progress states
        this.showProgressBar$ = this._progressSource
            .pipe(
                startWith(0),
                map(progress => !!progress),
                distinctUntilChanged(),
                pairwise(),
                switchMap(
                    ([previous, current]) => {
                        return of(current).pipe(delay(!previous && current ? TRANSITION_SPEED : 0));
                    },
                ),
            );

        // update progress according to the progress-bar transition speed
        this.progress$ = this._progressSource.pipe(auditTime(TRANSITION_SPEED));

        this.type = this.context.additionalData.type;

        // currently only CPU is supported, will add back GPU option when backend starts to support it
        this.processorTypes = [MLProcessor.CPU]
            .map(processor => ({
                id: processor,
                text: processor,
            }));

        this.inputTypes = [MLInputType.json, MLInputType.file, MLInputType.files]
            .map(inputVal => ({
                // The value that will be sent to the backend
                id: inputVal,
                // The value that will be displayed
                text: Object.keys(MLInputType).find(key => MLInputType[key as keyof typeof MLInputType] === inputVal)!,
            }));

        this.languages = [MLLanguage.PYTHON36, MLLanguage.PYTHON27]
            .map(language => ({
                // The value that will be sent to the backend
                id: Object.keys(MLLanguage).find(key => MLLanguage[key as keyof typeof MLLanguage] === language)!,
                // The value that will be displayed
                text: language,
            }));

        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            'name': [
                this.context.data.name,
                [
                    Validators.maxLength(UI_NAME_MLPACKAGE_MAX_LENGTH),
                    Validators.pattern(UI_MLPACKAGE_NAME_REGEX),
                    UiValidators.required(),
                ],
                [
                    this._validateUniqueMLPackageName,
                ],
            ],
            'inputType': [
                [this.context.data.inputType],
                UiValidators.required(),
            ],
            'inputDescription': [
                this.context.data.latestVersionInputDescription,
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'outputDescription': [
                this.context.data.latestVersionOutputDescription,
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'language': [
                this.languages,
                UiValidators.required(),
            ],
            'processor': [
                this.processorTypes,
                UiValidators.required(),
            ],
            'description': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'changeLog': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
        });

        this.isReadOnly$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(
                isReadOnly => isReadOnly ?
                    this.form.disable() :
                    this.form.enable(),
            );
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public onClear() {
        this.files = [];
    }

    public onFileChange(files: File[]) {
        if (files.length <= 1) {
            this.files = files;
        }
    }

    public onSubmit() {
        const areFilesValid = this.files.every(
            file => this._fileUploadService.isFileItemValid(file, this._allowedFileExtensions),
        );

        if (!areFilesValid) {
            this._snack.error(
                `${this._translateService.instant(
                    `mlpackage_upload_modal_file_input_warning`)} (#${ClientErrorCodes.InvalidFile})`,
            );
            return;
        }

        const [modelFile] = this.files;

        if (modelFile.size > this._maxModelSizeInByte) {
            this._snack.error(
                this._translateService.instant(`file_size_too_large`, {
                    maxSize: '5GB',
                }),
            );
            return;
        }

        this.loading$.next(true);
        this._progressSource.next(0);
        let signedUrl = ``;

        // disable the form while upload is in progress
        this.isReadOnly$.next(true);

        const upload$ = this._mlpackagesService.generateSignedUrlForModelUpload(modelFile.name).pipe(
            flatMap(response => {
                signedUrl = (response as ISignedUrlResponse).value;
                return this._mlpackagesService.uploadModelToSignedUrl(modelFile, signedUrl);
            }),
            skip(1),    // skip the first preflight request
            share(),
            catchError((err) => {
                this._handleUploadError();
                return of(err);
            }),
            takeUntil(this._destroyed$),
        );

        upload$.subscribe();

        // Handle upload process event
        upload$.pipe(
            filter(ev => ev.type === HttpEventType.UploadProgress && ev.total !== null),
        ).subscribe((ev) => {
            const processEvent = ev as HttpProgressEvent;
            this._progressSource.next(
                Math.round((processEvent.loaded * 100) / processEvent.total!),
            );
        });

        // Handle upload request completed
        upload$.pipe(
            filter(ev => ev.type === HttpEventType.Response),
            flatMap(() => {
                const payload: IMLPackage = {
                    ...cloneDeep(this.context.data),
                    modelVersions: [{
                        modelLanguage: this.form.get('language')!.value[0].id,
                        outputDescription: this.form.get('outputDescription')!.value,
                        inputDescription: this.form.get('inputDescription')!.value,
                        changeLog: this.form.get('changeLog')!.value,
                        processorType: this.form.get('processor')!.value[0].id,
                    }],
                    name: this.form.get('name')!.value,
                    description: this.form.get('description')!.value,
                };

                return this.isUploadingNewVersion ?
                    this._mlpackagesService.createPackageVersion(this.context.data.id, payload, signedUrl) :
                    this._mlpackagesService.createPackage(payload, signedUrl);
            }),
            catchError((err) => {
                this._handleUploadError();
                return of(err);
            }),
            delay(500),     // BUG: AIFBR-1919 - List MLPackages called immediately after a Create MLPackage
        ).subscribe(() => {
            this._closeDialog();
        });
    }

    private _handleUploadError() {
        this._snack.error(
            this._translateService.instant(`mlpackages_upload_failure`),
        );

        this._closeDialog();
    }

    private _closeDialog() {
        this._progressSource.next(0);
        this.loading$.next(false);

        this._dialogRef.close({
            type: ModalResultType.Add,
        });
    }

    private _validateUniqueMLPackageName = (control: AbstractControl) => {
        if (control.pristine || control.value == null) {
            return of(null);
        }

        return timer(500).pipe(
            switchMap(() => this._mlpackagesService.checkUniqueMLPackageName(control.value)),
            catchError(error => of(error)),
            pluck('error', 'respCode'),
            // 20005 means the ML package name is not unique
            map(respCode => respCode === 20005 ? { notUnique: true } : null),
            tap(() => this._cd.markForCheck()),
        );
    }
}

